import type { Game, Player, Member } from '../../types/game'
import { ResourceIcon } from '../shared/ResourceIcon'
import { PlayerMarker } from '../shared/PlayerMarker'
import { FlowerCard } from './FlowerCard'
import { assetUrl } from '../../utils/assets'

interface OpponentsTableauProps {
  game: Game
  myPlayerColor: string | null
  members: Member[]
}

const RESOURCES = ['nectar', 'pollen', 'honey', 'wax', 'propolis'] as const

function OpponentOverview({
  player,
  member,
  isVisible,
  isTurn,
}: {
  player: Player
  member: Member | null
  isVisible: boolean
  isTurn: boolean
}) {
  const displayName = member?.alias ?? player.color

  return (
    <div
      className={`opponent-overview ${isTurn ? 'turn' : ''}`}
      data-player-color={player.color}
      style={{ display: isVisible ? 'flex' : 'none', '--icon-bg': `var(--${player.color})` } as React.CSSProperties}
    >
      <div className="opponent-header">
        <div className="opponent-identity">
          <PlayerMarker color={player.color} className="opponent-marker" />
          <div className="opponent-name">{displayName}</div>
          {member?.is_host && (
            <img src={assetUrl('images', 'host.svg')} alt="Host" className="host-icon" />
          )}
        </div>
        {player.has_passed && <div className="passed-badge">passed</div>}
      </div>
      <div className="opponent-stats">
        <div className="opponent-resources">
          <div className="resource-pair" data-worker-source={`player-${player.color}`}>
            <ResourceIcon resource="worker_full" size="1.2em" />
            <div>{player.n_remaining_workers} / {player.n_workers}</div>
          </div>
          <div className="resource-pair">
            <ResourceIcon resource="egg" size="1.2em" />
            <div>{player.n_eggs}</div>
          </div>
          {RESOURCES.map((resource) => (
            <div key={resource} className="resource-pair">
              <ResourceIcon resource={resource} size="1.2em" />
              <div>
                {player.resources[resource] ?? 0}
                {resource === 'honey' && player.required_honey !== undefined && (
                  <span
                    className="honey-required-hint"
                    title="Honey this colony needs to feed its Workers at the Cold of Winter"
                  >
                    {' '}({player.required_honey})
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="opponent-flowers">
          {player.flowers.map((flower) => (
            <FlowerCard
              key={flower.id}
              flower={flower}
              workerColor={
                player.pollinated_flowers.some((pollinatedFlower) => pollinatedFlower.id === flower.id)
                  ? player.color
                  : undefined
              }
              selfPollinationSpent={player.spent_self_pollination_flower_ids?.includes(flower.id)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export function OpponentsTableau({ game, myPlayerColor, members }: OpponentsTableauProps) {
  const opponents = game.players

  return (
    <div className="opponents-tableau tableau">
      {opponents.map((player) => {
        const member = members.find((candidate) => candidate.color === player.color) ?? null
        return (
          <OpponentOverview
            key={player.color}
            player={player}
            member={member}
            isVisible={player.color !== myPlayerColor}
            isTurn={game.current_player.color === player.color}
          />
        )
      })}
    </div>
  )
}
